import { apiService } from '../../api/apiService';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { appName } from '../../api/environments';
import { ApiLocaleFile, ApiLocalizationData, GenericHttpResult } from '@agunity/api-v4';
import { defaultLanguageMessages, defaultLanguage } from '../../locales/default';
import { LanguageMessages } from '../../components/Language';
import { ApiLocaleDef } from '@agunity/api-v4/build/data-contracts';
import { RootState } from '../store';
import { HELP_LINK } from '../../Constants';

export interface LanguageState {
  language: string,
  localeMessage: LanguageMessages;
  localeOptions: ApiLocaleDef[];
  hubLocales: ApiLocaleDef[] | null;
  locale_newLocaleFileUploaded: boolean;
  locale_isOpenUnsavedModal: boolean;
  helpLink: string;
}

const initialState: LanguageState = {
  language: defaultLanguage,
  localeMessage: defaultLanguageMessages,
  localeOptions: [],
  hubLocales: null,
  locale_newLocaleFileUploaded: false,
  locale_isOpenUnsavedModal: false,
  helpLink: `${HELP_LINK}-${defaultLanguage}`
};

const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    setLanguage: (state: LanguageState, action: PayloadAction<string>) => {
      state.language = action.payload;
      state.helpLink = `${HELP_LINK}-${String(action.payload).slice(0, 2) || 'en'}`;
    },
    setLocaleMessage: (state: LanguageState, action: PayloadAction<LanguageMessages>) => {
      state.localeMessage = action.payload;
    },
    setLocaleOptions: (state: LanguageState, action: PayloadAction<ApiLocaleDef[]>) => {
      state.localeOptions = action.payload;
    },
    setHubLocales: (
      state: LanguageState,
      action: PayloadAction<ApiLocaleDef[] | null>
    ) => {
      state.hubLocales = action.payload;
    },
    setNewLocaleFileUploaded: (state: LanguageState, action: PayloadAction<boolean>) => {
      state.locale_newLocaleFileUploaded = action.payload;
    },
    setLocaleIsOpenUnsavedModal: (state: LanguageState, action: PayloadAction<boolean>) => {
      state.locale_isOpenUnsavedModal = action.payload;
    },
  },
});
export const { setLanguage, setHubLocales, setLocaleMessage, setLocaleOptions, setNewLocaleFileUploaded, setLocaleIsOpenUnsavedModal } = languageSlice.actions;

export const selectLocaleOptions = (state: RootState) => state.language.localeOptions;
export const selectLocale = (state: RootState) => state.language.language;

export const getLocales =
  () =>
    async (dispatch: Function) => {
      try {
        const response = await apiService.languageV20LocalesList();
        dispatch(setLocaleOptions(response.data || []));
        return response;
      }catch (e) {
        console.log(e);
      }
    };

export const getUnauthorizedLocale =
  (locale: string) =>
    async () => {
      const response = await apiService.uiUnauthorizedV10LocalizationDetail(appName, locale);
      return response;
    };

export const getFullLocale =
  (locale: string) =>
    async () => {
      const response = await apiService.uiV10LocalizationDetail(appName, locale);
      return response as unknown as GenericHttpResult<LanguageMessages>;
    };

export const saveLocal =
  (locale: string, data: LanguageMessages) =>
    async (
    ) => {
      const body: ApiLocaleFile = {
        body: JSON.stringify(data)
      };
      const response = await apiService.uiV10LocalizationCreate(appName, locale, body);
      return response;
    };

export const getLocalizationData =
  (locale: string) =>
    async () => {
      const response = await apiService.uiV10LocalizationFullDetail(appName, locale);
      return response as unknown as GenericHttpResult<ApiLocalizationData[]>;
    };

export const uploadLocalizationDataAPI =
  (locale: string, localeData: ApiLocalizationData[]) =>
    async () => {
      const response = await apiService.uiV10LocalizationFullCreate(appName, locale, localeData);
      return response as unknown as GenericHttpResult<void>;
    };

export const updateLocaleProfile =  (locale: string) => async () => {
  const response = await apiService.profileV10LocaleCreate(locale);
  return response;
};

export default languageSlice.reducer;